import { Reference } from '@apollo/client';

import useMutationWithValidation from '@hooks/useMutationWithValidation';
import {
  GetConsultDocument,
  GetConsultQuery,
  GetConsultQueryVariables,
  GetJourneyWithConsultsDocument,
  GetJourneyWithConsultsQuery,
  GetJourneyWithConsultsQueryVariables,
  InternalNoteCreateDocument,
  InternalNoteCreateMutation,
  InternalNoteCreateMutationVariables,
  InternalNoteFragmentDoc
} from '@typing/Generated';

type Args = {
  consultId?: string | null;
  journeyId: string;
};

const useCreateInternalNote = ({ consultId, journeyId }: Args) => {
  const [createInternalNote] = useMutationWithValidation<
    InternalNoteCreateMutation,
    InternalNoteCreateMutationVariables
  >(InternalNoteCreateDocument, 'internalNote', {
    update: (store, { data }) => {
      if (data?.internalNoteCreate?.internalNote) {
        //
        // We have to add it to the journey's list of internal notes
        //
        try {
          store.modify({
            fields: {
              internalNotes(cachedInternalNoteRefs: readonly Reference[]) {
                const newInternalNote = store.writeFragment({
                  data: data.internalNoteCreate?.internalNote,
                  fragment: InternalNoteFragmentDoc,
                  fragmentName: 'InternalNote'
                });
                if (newInternalNote && data.internalNoteCreate?.internalNote) {
                  return [...cachedInternalNoteRefs, newInternalNote];
                }
                return [...cachedInternalNoteRefs];
              }
            },
            id: store.identify({ __typename: 'Journey', id: journeyId })
          });
        } catch {
          // The query will raise an error if we haven't asked for the journey's internal notes
        }

        //
        // And we also have to add it to the initial session report
        //
        if (data?.internalNoteCreate?.internalNote.initialConsultReport?.id) {
          store.modify({
            fields: {
              internalNotes(cachedInternalNoteRefs: readonly Reference[]) {
                const newInternalNoteRef = store.writeFragment({
                  data: data.internalNoteCreate?.internalNote,
                  fragment: InternalNoteFragmentDoc,
                  fragmentName: 'InternalNote'
                });
                if (newInternalNoteRef && data.internalNoteCreate?.internalNote) {
                  return [...cachedInternalNoteRefs, newInternalNoteRef];
                }

                return [...cachedInternalNoteRefs];
              }
            },
            id: store.identify({ ...data.internalNoteCreate.internalNote.initialConsultReport })
          });
        }

        //
        // And we also have to add it to the session report
        //
        if (data.internalNoteCreate?.internalNote?.consultReport?.id) {
          try {
            const consultsCacheData = store.readQuery<
              GetJourneyWithConsultsQuery,
              GetJourneyWithConsultsQueryVariables
            >({
              query: GetJourneyWithConsultsDocument,
              variables: { id: journeyId }
            });

            if (consultsCacheData) {
              let consults = consultsCacheData.journey.consults ?? [];
              const consultIndex = consults.findIndex(
                consult => consult.id === data.internalNoteCreate?.internalNote?.consultReport?.id
              );
              const consult = consults[consultIndex];
              if (consult.consultReport) {
                consults = [...consults];
                consults.splice(consultIndex, 1);
                const notes = consult.consultReport?.internalNotes ?? [];
                const newCacheData = {
                  ...consultsCacheData,
                  journey: {
                    ...consultsCacheData.journey,
                    consults: [
                      ...consults,
                      {
                        ...consult,
                        consultReport: {
                          ...consult.consultReport,
                          internalNotes: [...notes, data.internalNoteCreate.internalNote]
                        }
                      }
                    ]
                  }
                };
                store.writeQuery<GetJourneyWithConsultsQuery, GetJourneyWithConsultsQueryVariables>({
                  data: newCacheData,
                  query: GetJourneyWithConsultsDocument
                });
              }
            }
          } catch {
            // The query will raise an error if we haven't asked for the journey's consults
          }
        }

        //
        // And if we've gotten the consult directly then we need to add it to that.
        //
        if (consultId && data.internalNoteCreate?.internalNote?.consultReport?.id) {
          try {
            const consultCacheData = store.readQuery<GetConsultQuery, GetConsultQueryVariables>({
              query: GetConsultDocument,
              variables: { id: consultId }
            });

            if (consultCacheData) {
              const consult = consultCacheData.consult;
              if (consult.consultReport) {
                const notes = consult.consultReport?.internalNotes ?? [];
                const newCacheData = {
                  ...consultCacheData,
                  consult: {
                    ...consult,
                    consultReport: {
                      ...consult.consultReport,
                      internalNotes: [...notes, data.internalNoteCreate.internalNote]
                    }
                  }
                };
                store.writeQuery<GetConsultQuery, GetConsultQueryVariables>({
                  data: newCacheData,
                  query: GetConsultDocument,
                  variables: { id: consultId }
                });
              }
            }
          } catch {
            // The query will raise an error if we haven't asked for the journey's consults
          }
        }
      }
    }
  });
  return createInternalNote;
};

export default useCreateInternalNote;
